import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { noAutofillProps } from '@/components/ui/no-autofill';
import { SecretInput } from '@/components/ui/secret-input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MapPin, Package, Users, CheckCircle2, ArrowRight, Store } from 'lucide-react';
import LocationFormModal from '@/features/locations/components/LocationFormModal';
import { useLocations } from '@/features/locations/hooks/useLocations';
import { useOrganization } from '@/contexts/OrganizationContext';
import { ShopifySetupInstructions } from './ShopifySetupInstructions';
import { trpc, type RouterInputs } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';

type UpdateOrganizationInput = RouterInputs['organization']['updateOrganization'];

interface OnboardingModalProps {
  open: boolean;
  onComplete: () => void;
}

const ONBOARDING_STEPS = [
  {
    icon: Store,
    title: 'Welcome to ThreadHeads!',
    description: 'Let\'s get your organization connected to Shopify in just a few steps',
    action: 'welcome',
    required: true,
  },
  {
    icon: Store,
    title: 'Connect Shopify',
    description: 'Create a custom app in Shopify and connect your store',
    action: 'shopify_setup',
    required: true,
  },
  {
    icon: MapPin,
    title: 'Add Your First Location',
    description: 'Set up your warehouse or retail location to start tracking inventory',
    action: 'location',
    required: true,
  },
  {
    icon: Package,
    title: 'Configure Inventory',
    description: 'Add your products and stock levels',
    action: 'skip',
    required: false,
  },
  {
    icon: Users,
    title: 'Invite Team Members',
    description: 'Add users to collaborate on inventory management',
    action: 'skip',
    required: false,
  },
];

export default function OnboardingModal({ open, onComplete }: OnboardingModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const { locations } = useLocations();
  const { organization, reloadOrganization } = useOrganization();
  const utils = trpc.useUtils();

  // Shopify credentials form state — Client ID + Secret from the Dev
  // Dashboard app; the backend exchanges them for the access token.
  const [shopifyDomain, setShopifyDomain] = useState('');
  const [shopifyClientId, setShopifyClientId] = useState('');
  const [shopifyClientSecret, setShopifyClientSecret] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const shopifyCredentialsValid =
    shopifyDomain.trim() !== '' &&
    shopifyClientId.trim() !== '' &&
    shopifyClientSecret.trim() !== '';

  // tRPC mutation for updating organization
  const updateOrganization = trpc.organization.updateOrganization.useMutation({
    onSuccess: (_result, _variables: UpdateOrganizationInput) => {
      // Invalidate tRPC query and reload OrganizationContext
      utils.organization.getOrganization.invalidate();
      reloadOrganization();
      toast({
        title: 'Success',
        description: 'Shopify credentials saved successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save Shopify credentials',
        variant: 'destructive',
      });
    },
  });

  // Auto-advance when location is created (step 2)
  useEffect(() => {
    if (locations.length > 0 && currentStep === 2) {
      setCurrentStep(3);
    }
  }, [locations, currentStep]);

  // New-flow orgs (BLU-1376, D8) set up through /onboarding and never see this
  // Client ID / Secret form, before or after they finish. Placed after every
  // hook, so it covers every caller (the Inventory auto-open, the Orders
  // Complete setup link) and any future one.
  if (organization?.setup_required === true) return null;

  const currentStepData = ONBOARDING_STEPS[currentStep];

  const handleSaveShopifyCredentials = async () => {
    if (!organization) return;

    setIsSubmitting(true);
    try {
      const payload: UpdateOrganizationInput = {
        shopifyDomain: shopifyDomain,
        shopifyClientId: shopifyClientId.trim(),
        shopifyClientSecret: shopifyClientSecret.trim(),
      };

      await updateOrganization.mutateAsync(payload);
      // Save progress to localStorage
      localStorage.setItem('onboarding_shopify_completed', 'true');
      handleNext();
    } catch (_error) {
      // Error handled by mutation onError callback (shows toast)
      // No need to log to console
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAction = async () => {
    if (!currentStepData) return;
    
    switch (currentStepData.action) {
      case 'welcome':
        handleNext();
        break;
      case 'shopify_setup':
        await handleSaveShopifyCredentials();
        break;
      case 'location':
        setLocationModalOpen(true);
        break;
      default:
        handleNext();
        break;
    }
  };

  const handleNext = () => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleSkipAll = () => {
    onComplete();
  };

  // Check if current step can proceed
  const canProceed = () => {
    if (!currentStepData) return false;
    
    switch (currentStepData.action) {
      case 'welcome':
        return true;
      case 'shopify_setup':
        return shopifyCredentialsValid;
      case 'location':
        return locations.length > 0;
      default:
        return true;
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col" hideCloseButton>
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-2xl">Welcome to ThreadHeads! 👋</DialogTitle>
            <DialogDescription>
              Let's get your organization set up in just a few steps
            </DialogDescription>
          </DialogHeader>

          <div className="py-6 overflow-y-auto flex-1">
            {/* Progress indicator */}
            <div className="flex items-center justify-center mb-8 gap-2">
              {ONBOARDING_STEPS.map((_step, index) => (
                <React.Fragment key={index}>
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors ${
                      index < currentStep
                        ? 'bg-primary border-primary text-primary-foreground'
                        : index === currentStep
                        ? 'border-primary text-primary'
                        : 'border-muted text-muted-foreground'
                    }`}
                  >
                    {index < currentStep ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <span className="text-sm font-medium">{index + 1}</span>
                    )}
                  </div>
                  {index < ONBOARDING_STEPS.length - 1 && (
                    <div
                      className={`w-12 h-0.5 transition-colors ${
                        index < currentStep ? 'bg-primary' : 'bg-muted'
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Current step content */}
            <Card className="border-2">
              <CardContent className="pt-6">
                {/* Step 0: Welcome */}
                {currentStep === 0 && currentStepData && (
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="p-4 rounded-full bg-primary/10">
                      {React.createElement(currentStepData.icon, {
                        className: 'h-8 w-8 text-primary',
                      })}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">
                        {currentStepData.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {currentStepData.description}
                      </p>
                    </div>
                    <div className="w-full text-left space-y-2 pt-4">
                      <p className="text-sm">
                        As an administrator, you'll need to:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                        <li>Connect your Shopify store</li>
                        <li>Enable 5‑minute order sync for inventory accuracy</li>
                        <li>Add your first warehouse/retail location</li>
                      </ul>
                      <p className="text-sm text-muted-foreground pt-2">
                        This should only take a few minutes!
                      </p>
                    </div>
                    <Button onClick={handleAction} className="w-full" size="lg">
                      Get Started
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                )}

                {/* Step 1: Shopify Credentials */}
                {currentStep === 1 && currentStepData && (
                  <div className="space-y-4">
                    <div className="flex flex-col items-center text-center space-y-2">
                      <div className="p-4 rounded-full bg-primary/10">
                        {React.createElement(currentStepData.icon, {
                          className: 'h-8 w-8 text-primary',
                        })}
                      </div>
                      <h3 className="text-xl font-semibold">
                        {currentStepData.title}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {currentStepData.description}
                      </p>
                    </div>

                    {/* Shopify Setup Instructions */}
                    <div className="max-h-[300px] overflow-y-auto">
                      <ShopifySetupInstructions />
                    </div>

                    {/* Credentials Form */}
                    <div className="space-y-4 pt-4 border-t">
                      <div className="space-y-2">
                        <Label htmlFor="shopify_domain">Shopify Domain *</Label>
                        <Input
                          id="shopify_domain"
                          placeholder="yourstore.myshopify.com"
                          value={shopifyDomain}
                          onChange={(e) => setShopifyDomain(e.target.value)}
                          disabled={isSubmitting}
                        />
                        <p className="text-xs text-muted-foreground">
                          Your store URL (e.g., threadheads.myshopify.com)
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="client_id">Client ID *</Label>
                        <Input
                          id="client_id"
                          placeholder="From your Dev Dashboard app"
                          value={shopifyClientId}
                          onChange={(e) => setShopifyClientId(e.target.value)}
                          disabled={isSubmitting}
                          {...noAutofillProps}
                        />
                        <p className="text-xs text-muted-foreground">
                          From Step 3 above
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="client_secret">Client Secret *</Label>
                        <SecretInput
                          id="client_secret"
                          placeholder="Paste the Client Secret"
                          value={shopifyClientSecret}
                          onChange={(e) => setShopifyClientSecret(e.target.value)}
                          disabled={isSubmitting}
                        />
                        <p className="text-xs text-muted-foreground">
                          We exchange these for an access token and keep it refreshed automatically
                        </p>
                      </div>

                      <Alert>
                        <AlertDescription className="text-xs">
                          Your credentials are encrypted and stored securely. They will never be displayed in the UI after saving.
                        </AlertDescription>
                      </Alert>

                      <Button
                        onClick={handleAction}
                        className="w-full"
                        size="lg"
                        disabled={!canProceed() || isSubmitting}
                      >
                        {isSubmitting ? 'Saving...' : 'Save & Continue'}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 2: Add Location */}
                {currentStep === 2 && currentStepData && (
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="p-4 rounded-full bg-primary/10">
                      {React.createElement(currentStepData.icon, {
                        className: 'h-8 w-8 text-primary',
                      })}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">
                        {currentStepData.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {currentStepData.description}
                      </p>
                    </div>

                    {locations.length > 0 && (
                      <div className="w-full p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2 text-green-700">
                          <CheckCircle2 className="h-5 w-5" />
                          <span className="font-medium">
                            Great! Your first location has been created.
                          </span>
                        </div>
                      </div>
                    )}

                    {locations.length === 0 ? (
                      <Button onClick={handleAction} className="w-full" size="lg">
                        Add Location
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    ) : (
                      <Button onClick={handleNext} className="w-full" size="lg">
                        Continue
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}

                {/* Steps 3-4: Optional steps */}
                {currentStep >= 3 && currentStepData && (
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="p-4 rounded-full bg-primary/10">
                      {React.createElement(currentStepData.icon, {
                        className: 'h-8 w-8 text-primary',
                      })}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">
                        {currentStepData.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {currentStepData.description}
                      </p>
                    </div>
                    <div className="flex gap-3 w-full">
                      <Button
                        variant="outline"
                        onClick={handleNext}
                        className="flex-1"
                        size="lg"
                      >
                        Skip for now
                      </Button>
                      <Button onClick={handleAction} className="flex-1" size="lg">
                        {currentStep === ONBOARDING_STEPS.length - 1
                          ? 'Finish'
                          : 'Continue'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Skip all link */}
            <div className="text-center mt-6">
              <button
                onClick={handleSkipAll}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors underline"
              >
                Skip setup and explore on my own
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Location form modal */}
      <LocationFormModal
        open={locationModalOpen}
        onOpenChange={setLocationModalOpen}
        location={null}
      />
    </>
  );
}
